import { Injectable } from '@angular/core';
import { Configuration, ConfigurationOrder } from '@ess/jg-rule-executor';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { InitDataService } from '@src/app/services/data/init-data.service';
import { ProductImageService } from '@src/app/services/product-image/product-image.service';
import { AssetService } from '@src/app/services/asset/asset.service';
import { firstValueFrom } from 'rxjs';
import { Icon } from '@src/app/library/components/icon';
import { NotificationService } from '../notification/notification.service';
import { NotificationCardType } from '@src/app/components/notification-card/notification-card.type';
import { Scene } from 'three';
import { LoggingService } from '@src/app/services/logging/logging.service';
import { WebhookFlowType } from '@src/app/generated/globalTypes';
import { GammaHandlerService } from '../retailer-handlers/gamma-handler.service';
import { JSON } from 'ta-json';
import { ModalService } from 'src/app/services/modal/modal.service';
import { CombinedGraphQLErrors } from '@apollo/client';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private _window: Window = window;

  constructor(
    private _dataService: InitDataService,
    private _apolloService: ApolloService,
    private _productImageService: ProductImageService,
    private _assetService: AssetService,
    private _notificationService: NotificationService,
    private _loggingService: LoggingService,
    private _gammaHandlerService: GammaHandlerService,
    private _modalService: ModalService,
  ) {
  }

  /**
   * Calls apollo service to send a configuration to be ordered to the backend. Returns ConfigurationOrder w/ purchase details.
   */
  public async placeConfigurationOrder$(
    configuration: Configuration,
    price: number,
    scene: Scene,
  ): Promise<void> {
    const publishedModuleVariantsByVariantId = new Map(
      this._dataService.inConfigAndPublishedModuleVariants.map(fv => [fv.variant.id, fv]),
    );

    const placementInfo = configuration.configurationPlacement
      .getPlacementsInfo(publishedModuleVariantsByVariantId);

    // do request
    let configurationOrder: ConfigurationOrder;
    try {
      configurationOrder = await firstValueFrom(
        this._apolloService.placeConfigurationOrder$(configuration, price, placementInfo)
      );
    } catch (e) {
      const code: string | undefined = (CombinedGraphQLErrors.is(e))
        ? e.errors[0].extensions.code as string
        : undefined;
      if (code?.endsWith('noFreeEANCode')) {
        this._notificationService.showNotification('app.cart.noFreeEANCode', Icon.warn, NotificationCardType.warning);
      } else {
        this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
      }
      return;
    }

    // check if order is valid and present
    if (!configurationOrder) {
      this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
      return;
    }

    // Make a product image for the shopping cart (for now, we skip this on iOS as it causes memory/GPU issues)
    if (!this._dataService.isIos) {
      await this._productImageService
        .generateProductImage$(scene)
        .then(image => this._assetService.uploadConfigurationOrderImage$(image, configurationOrder.id))
        .catch(e => this._loggingService.error(new Error(`Could not generate product image for configuration order: ${e}`)));
    }

    const flowType = this._dataService.currentRetailerOptions.webhookOptions.flowType;
    let payload: string;
    if (flowType == WebhookFlowType.directbase64encodedpayload
      || flowType == WebhookFlowType.gammasendmessage
      || flowType == WebhookFlowType.hornbachsendmessage
    ) {
      // This JSON payload is still encoded so will first need to be JSON parsed
      payload = await firstValueFrom(this._apolloService.getPayloadForOrder$(configurationOrder.id));
    }

    if (configurationOrder.price + (configurationOrder.installationCost ?? 0) === price) {
      switch (flowType) {
        case WebhookFlowType.codegetredirect:
          if (configurationOrder.code) {
            this._codeGetRedirect(configurationOrder.code);
          } else {
            this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
          }
          break;
        case WebhookFlowType.directbase64encodedpayload:
          if (payload) {
            this._shopRedirect(payload);
          } else {
            this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
          }
          break;
        case WebhookFlowType.gammasendmessage:
          this._gammaHandlerService.parseAndSendMessage(payload);
          break;
        case WebhookFlowType.hornbachsendmessage:
          // We abuse the payload to put in the url which we receive from the Hornbach Integration Service
          if (payload) {
            console.log('Opening Hornbach redirect URL:', payload);
            this._window.open(JSON.parse(payload), '_self');
          } else {
            this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
          }
          break;
      }
    } else {
      this._notificationService.showNotification('app.cart.priceError', Icon.warn, NotificationCardType.warning);
    }
  }

  private _shopRedirect(payload: string): void {
    setTimeout(() => {
      try {
        const parsedPayload: string = JSON.parse(payload);
        const redirectUrl = new URL(this._dataService.currentRetailerOptions.webhookOptions.webhookUrls.redirectUrl);
        redirectUrl.searchParams.append('data', parsedPayload);

        if (this._dataService.isIos) {
          this._showRedirectModal(redirectUrl);
        } else {
          this._window.open(redirectUrl.toString(), '_blank');
        }
      } catch (e) {
        console.error(e); // for sentry logging
        this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
      }
    });
  }

  /**
   * Redirects to the redirectUrl from the retailerOptions, opening in a new tab, preserving any existing query
   * parameters on the current page as well as parameters present from the redirectUrl, and adding the configCode
   * as a parameter.
   */
  private _codeGetRedirect(code: string): void {
    setTimeout(() => {
      try {
        const existingParams = new URL(this._window.location.href).searchParams;
        const redirectUrl = new URL(this._dataService.currentRetailerOptions.webhookOptions.webhookUrls.redirectUrl);
        existingParams.forEach((value, key) => redirectUrl.searchParams.append(key, value));
        redirectUrl.searchParams.append('configCode', code);

        if (this._dataService.isIos) {
          this._showRedirectModal(redirectUrl);
        } else {
          this._window.open(redirectUrl.toString(), '_blank');
        }
      } catch (e) {
        console.error(e); // for sentry logging
        this._notificationService.showNotification('app.cart.error', Icon.warn, NotificationCardType.warning);
      }
    });
  }

  private _showRedirectModal(url: URL): void {
    this._modalService.open('redirect-modal', { url: url });
  }
}
